import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CheckIcon from "@mui/icons-material/Check";
import Tooltip from "@mui/material/Tooltip";
import CircularProgress from "@mui/material/CircularProgress";
import CustomButton from "components/Screens/Marketplace/Customer/common/Button";
import VideoModal from "components/Screens/Marketplace/Customer/common/VideoModal";
import WatchIntroButton from "components/Screens/Marketplace/Customer/ProfileSection/WatchIntroButton";
import HearNameButton from "components/Screens/Marketplace/Customer/ProfileSection/HearNameButton";
import ProfileTabs from "components/Screens/Marketplace/Customer/common/ProfileTabs";
import ProfileContent, {
  getAvailableTabs,
} from "components/Screens/Marketplace/Customer/ProfileSection/ProfileContent";
import InterviewSuccessModal from "components/Screens/Marketplace/Customer/TalentNetwork/InterviewSuccessModal";
import ShortlistButton from "components/Screens/Marketplace/Customer/ProfileSection/ShortlistButton";
import useProfileViewTracking from "components/Screens/Marketplace/Customer/ProfileSection/useProfileViewTracking";
import useTalentApi from "components/Screens/Marketplace/Customer/TalentApiContext";
import { analytics } from "services/analytics";
import type { AiSummaryCapability, AiSummarySentiment, TalentProfile } from "components/Screens/Marketplace/Customer/types";
import getTalentFullName from "components/Screens/Marketplace/Customer/utils/talentName";
import SaveToShortlistButton from "components/Screens/Marketplace/Customer/Shortlist/SaveToShortlistButton";
import { LockedControl } from "components/Screens/Marketplace/Customer/common/Withheld";
import { WITHHELD_TIPS } from "components/Screens/Marketplace/Customer/common/Withheld/Withheld";
import { getTalentDisplayName, isWithheld, talentPicture } from "components/Screens/Marketplace/Customer/utils/redaction";
import usePlgSelfServe from "hooks/usePlgSelfServe";


import layer1 from "components/Screens/Marketplace/Customer/assets/video-layers/layer1.svg";
import layer2Left from "components/Screens/Marketplace/Customer/assets/video-layers/layer2-left.svg";
import layer2Right from "components/Screens/Marketplace/Customer/assets/video-layers/layer2-right.svg";
import layer4Left from "components/Screens/Marketplace/Customer/assets/video-layers/layer4-left.svg";
import layer4Right from "components/Screens/Marketplace/Customer/assets/video-layers/layer4-right.svg";
import layer2LeftMobile from "components/Screens/Marketplace/Customer/assets/video-layers/layer2-left-mobile.svg";
import layer2RightMobile from "components/Screens/Marketplace/Customer/assets/video-layers/layer2-right-mobile.svg";
import coverImage from "components/Screens/Marketplace/Customer/assets/common/cover.jpg";

import "components/Screens/Marketplace/Customer/common/Withheld/Withheld.scss";
import "components/Screens/Marketplace/Customer/ProfileSection/ProfileSection.scss";

// Provide a safe fallback in non-webpack environments (e.g., Jest)
import PakistanFlag from "assets/svgs/PakistanFlag";
import CostaRicaFlag from "assets/svgs/CostaRicaFlag";
import PeruFlag from "assets/svgs/PeruFlag";
import { trackPlg } from "plg/analytics/events";
import { plgToolMatch } from "plg/analytics/toolMatch";
import type { LockedElement } from "plg/analytics/events";
import { currentPlgTier } from "plg/analytics/identity";
import { countProfileView, takeListContext } from "plg/analytics/plgAnalyticsSession";
import experienceLabel from "components/Screens/Marketplace/Customer/utils/experienceLabel";

const FLAG_COMPONENTS: Record<string, React.ComponentType<{ width?: string; height?: string }>> = {
  Pakistan: PakistanFlag,
  "Costa Rica": CostaRicaFlag,
  Peru: PeruFlag,
};

interface ProfileSectionProps {
  className?: string;
  profileId?: string;
  initialTalent: TalentProfile; // now required
}

// The header pins 56px from the top (ProfileSection.scss __sticky top); the full-size photo sits 50px above it (__user-image top).
const STICKY_TOP_PX = 56;
const PHOTO_OVERHANG_PX = 50;

function ProfileSection({ className = "", profileId, initialTalent }: ProfileSectionProps) {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isStickyActive, setIsSticky] = useState(false);
  // Phones: the pinned name / Shortlist row hides while scrolling down and comes back on scroll up.
  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(false);
  const [interviewRequested, setInterviewRequested] = useState(false);
  const [isRequestingInterview, setIsRequestingInterview] = useState(false);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const stickyRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // eslint-disable-next-line no-underscore-dangle
  const talentId = initialTalent?._id;
  // Never send a withheld surname to analytics: the display name already accounts for redaction.
  const talentName = getTalentDisplayName(initialTalent);
  const displayName = talentName;

  useEffect(() => {
    if (!talentId) return;
    const linkToken = new URLSearchParams(location.search).get("token") || undefined;
    analytics.track("Talent Profile Viewed", {
      talentId,
      talentName,
      ...(linkToken ? { linkToken } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talentId]);

  // eslint-disable-next-line no-underscore-dangle
  useProfileViewTracking(initialTalent?._id);

  const availableTabs = useMemo(() => getAvailableTabs(initialTalent), [initialTalent]);

  const api = useTalentApi();
  const plg = api.kind === "plg";

  useEffect(() => {
    if (!talentId || !plg) return;
    const list = takeListContext(talentId);
    const props = {
      talent_id: talentId,
      ...(list?.role ? { role: list.role } : {}),
      ...(list ? { position_in_list: list.position } : {}),
      tier: currentPlgTier(),
      session_view_count: countProfileView(),
    };
    plgToolMatch(initialTalent?.tools?.map((t) => t.name)).then((tool_match) => trackPlg("profile_viewed", { ...props, tool_match }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talentId]);

  // Redaction is PLG-only, so a locked control is always a PLG click.
  const lockedClick = (element: LockedElement) => () => trackPlg("locked_element_clicked", { element, talent_id: talentId });
  const glanceExpanded = plg && talentId ? () => trackPlg("glance_expanded", { talent_id: talentId }) : undefined;
  // The vote goes to the portal backend, which adds the customer and user and forwards it to marketplace.
  const glanceFeedback = async (sentiment: AiSummarySentiment, capabilities: AiSummaryCapability[]) => {
    if (!talentId) return false;
    const shownAt = initialTalent?.aiSummaryGeneratedAt;
    const generatedAt: Partial<Record<AiSummaryCapability, string>> = {};
    if (capabilities.includes("recruiter_pitch") && shownAt?.recruiterPitch) generatedAt.recruiter_pitch = shownAt.recruiterPitch;
    if (capabilities.includes("personality_snapshot") && shownAt?.personalitySnapshot) generatedAt.personality_snapshot = shownAt.personalitySnapshot;
    const result = await api.submitAiSummaryFeedback(talentId, {
      sentiment,
      capabilities,
      ...(Object.keys(generatedAt).length ? { generatedAt } : {}),
    });
    return result.ok;
  };
  const canRequestInterview = api.hasPermission("talentNetwork.request_interview");
  const shortlistMode = usePlgSelfServe();
  const pdfLocked = isWithheld(initialTalent, "pdf");
  const picture = talentPicture(initialTalent);
  const introLocked = isWithheld(initialTalent, "introVideo") && !!initialTalent?.hasIntroVideo;
  // Portal only, with a recording and full access: PLG customers (verified or redacted) get no speaker at all
  // (product 2026-10-28, 2026-10-31).
  const hearNameUrl =
    plg || isWithheld(initialTalent, "namePronunciation") ? undefined : initialTalent?.namePronunciationUrl;
  const initials = `${initialTalent?.firstName?.[0] || ""}${
    initialTalent?.lastInitial || initialTalent?.lastName?.[0] || ""
  }`.toUpperCase();

  const FlagComponent = initialTalent?.country ? FLAG_COMPONENTS[initialTalent.country] : undefined;

  const loggedInUser = api.user;

  useEffect(() => {
    const query = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  // Check if interview already requested for this talent
  useEffect(() => {
    const checkRequested = async () => {
      // eslint-disable-next-line no-underscore-dangle
      if (!loggedInUser?.customerId || !initialTalent?._id) return;
      const requested = await api.checkInterviewRequested(
        loggedInUser.customerId,
        // eslint-disable-next-line no-underscore-dangle
        initialTalent._id
      );
      setInterviewRequested(requested);
    };
    checkRequested();
    // eslint-disable-next-line react-hooks/exhaustive-deps, no-underscore-dangle
  }, [initialTalent?._id]);

  useEffect(() => {
    const el = stickyRef.current;
    if (!el) return undefined;

    // Use IntersectionObserver with a sentinel element positioned right above sticky element
    const sentinel = document.createElement("div");
    sentinel.style.position = "absolute";
    sentinel.style.left = "0";
    sentinel.style.width = "1px";
    sentinel.style.height = "1px";
    sentinel.style.top = "-1px"; // Just above the sticky element
    sentinel.style.pointerEvents = "none";
    sentinel.setAttribute("data-sticky-sentinel", "true");

    el.appendChild(sentinel);

    // Phones: where the header pins (its CSS top). The portal pins it at 0 and keeps the plain line; PLG pins it
    // under its top bar, so the line moves down to match and the compact header turns on once pinned.
    const phonePinTop = parseFloat(getComputedStyle(el).top) || 0;
    const phoneLine = phonePinTop ? `-${phonePinTop + 1}px 0px 0px 0px` : "0px";

    // isStickyActive = the header is still below the pin line (full-size photo); false = compact.
    // Set from the sentinel's position, not toggled, so a missed or doubled callback can't leave it the
    // wrong way round. On desktop the line is the 56px pin point plus the photo's 50px overhang: the
    // full-size photo hangs 50px above the header row and would otherwise rise over the top bar in the
    // last 50px before the header pins.
    const observer = new IntersectionObserver(
      (entries) => {
        const latest = entries[entries.length - 1];
        if (latest) setIsSticky(latest.isIntersecting);
      },
      {
        threshold: [0, 1],
        // rootMargin negative value = offset from viewport top
        rootMargin: isMobile ? phoneLine : `-${STICKY_TOP_PX + PHOTO_OVERHANG_PX}px 0px 0px 0px`,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
      if (sentinel.parentNode) {
        sentinel.parentNode.removeChild(sentinel);
      }
    };
    // Rebuilt when the phone breakpoint flips: isMobile is set after mount, so a mount-only observer
    // always kept the desktop line on phones.
  }, [isMobile]);

  const handlePlayVideo = () => {
    if (initialTalent?.introVideoUrl) {
      setIsVideoModalOpen(true);
      analytics.track("Talent Video Opened", { talentId, talentName });
    }
  };

  const handleCloseVideo = () => {
    setIsVideoModalOpen(false);
    analytics.track("Talent Video Playback Exited", { talentId, talentName });
  };

  const handleRequestInterview = useCallback(async () => {
    if (!loggedInUser || !initialTalent || interviewRequested || isRequestingInterview) return;
    setIsRequestingInterview(true);
    try {
      await api.requestInterview({
        customerId: loggedInUser.customerId,
        customerUserName: loggedInUser.name || "",
        customerUserEmail: loggedInUser.email || "",
        customerUserId: loggedInUser.userId || "",
        // eslint-disable-next-line no-underscore-dangle
        talentId: initialTalent._id,
        talentName: getTalentFullName(initialTalent),
        // eslint-disable-next-line no-underscore-dangle
        talentProfileUrl: `${window.location.origin}${api.routes.talentProfile(initialTalent._id)}`,
        requestedAt: new Date().toLocaleString("en-US", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        }),
      });
      setInterviewRequested(true);
      analytics.track("Talent Interview Requested", { talentId, talentName });
      setShowInterviewModal(true);
    } catch {
      // silently fail
    } finally {
      setIsRequestingInterview(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser, initialTalent, interviewRequested, isRequestingInterview, api]);

  const handleDownloadPDF = async () => {
    if (!profileId) {
      return;
    }

    try {
      // eslint-disable-next-line no-underscore-dangle
      const { blob, filename } = await api.fetchTalentPdf(initialTalent?._id || profileId);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      analytics.track("Talent Profile PDF Downloaded", { talentId, talentName });
    } catch (error) {
      // swallow
    }
  };

  useEffect(() => {
    const pinned = isMobile && !isStickyActive;
    if (!pinned) {
      setIsHeaderCollapsed(false);
      return undefined;
    }
    let lastY = window.scrollY;
    let frame = 0;
    let collapsed = false;
    // The row's own height change nudges the scroll position (scroll anchoring); ignore that settle.
    let settleUntil = 0;
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const y = window.scrollY;
        if (performance.now() < settleUntil) {
          lastY = y;
          return;
        }
        // Small moves (and the iOS bounce) don't toggle it.
        if (Math.abs(y - lastY) < 12) return;
        const next = y > lastY;
        lastY = y;
        if (next === collapsed) return;
        collapsed = next;
        settleUntil = performance.now() + 350;
        setIsHeaderCollapsed(next);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [isMobile, isStickyActive]);

  const hideBadges = isMobile && isStickyActive;

  return (
    <>
      <div className={`profile-section ${className}`}>
        <div className="profile-section__timeline">
          <div className="profile-section__timeline-background">
            {initialTalent?.introVideoUrl ? (
              <>
                <img
                  src={layer1}
                  alt="Layer 1 Background"
                  className="profile-section__layer profile-section__layer--1-background"
                />

                <video
                  className="profile-section__video-thumbnail"
                  src={initialTalent?.introVideoUrl}
                  muted
                  playsInline
                  preload="metadata"
                />

                <img
                  src={isMobile ? layer2LeftMobile : layer2Left}
                  alt="Layer 2 Left Gradient"
                  className="profile-section__layer profile-section__layer--2-left"
                />
                <img
                  src={isMobile ? layer2RightMobile : layer2Right}
                  alt="Layer 2 Right Gradient"
                  className="profile-section__layer profile-section__layer--2-right"
                />

                <img
                  src={layer4Left}
                  alt="Layer 4 Left Pattern"
                  className="profile-section__layer profile-section__layer--4-left"
                />
                <img
                  src={layer4Right}
                  alt="Layer 4 Right Pattern"
                  className="profile-section__layer profile-section__layer--4-right"
                />
              </>
            ) : (
              <img
                src={coverImage}
                alt="Layer 1 Background"
                className="profile-section__layer profile-section__layer--1-background"
              />
            )}

            {/* Verified: the video's own frame behind the button, which plays it in the modal (prototype .video-btn) */}
            {initialTalent?.introVideoUrl && (
              <div className="profile-section__watch">
                <WatchIntroButton onClick={handlePlayVideo} />
              </div>
            )}

            {/* Redacted (prototype .video.is-blurred): the person's avatar on the cover and the same button, which never
                plays; hovering or tapping it says when it unlocks (the Hidden chip tip) */}
            {introLocked && (
              <>
                <div className="profile-section__intro-poster" aria-hidden>
                  {picture && <img src={picture} alt="" />}
                </div>
                <div className="profile-section__watch">
                  <Tooltip
                    title={
                      <>
                        <b>{WITHHELD_TIPS.video[0]}</b>
                        {WITHHELD_TIPS.video[1]}
                      </>
                    }
                    placement="top"
                    enterTouchDelay={0}
                    leaveTouchDelay={4000}
                    slotProps={{ tooltip: { className: "hidden-chip-tip" } }}
                  >
                    <span>
                      <WatchIntroButton locked onClick={lockedClick("intro_video")} />
                    </span>
                  </Tooltip>
                </div>
              </>
            )}
          </div>
        </div>

        <div
          ref={stickyRef}
          className={`profile-section__sticky ${!isStickyActive ? "profile-section__sticky--active" : ""} ${
            isHeaderCollapsed ? "profile-section__sticky--collapsed" : ""
          }`}
        >
          <div className="profile-section__info">
            {/* User Image overlapping timeline */}
            <div className="profile-section__user-image">
              {/* No picture (none uploaded, or withheld with no illustrated avatar): initials, never a broken image.
                  A withheld photo shows the illustrated avatar for the talent's gender (product 2026-10-23). */}
              {picture ? (
                <span className="profile-section__avatar-frame">
                  <img src={picture} alt={displayName} className="profile-section__avatar" />
                </span>
              ) : (
                <div className="profile-section__avatar profile-section__avatar--initials">{initials}</div>
              )}
            </div>

            {/* User Details */}
            <div className="profile-section__details">
              <div className="profile-section__name-section">
                <h1 className="profile-section__name">
                  <span className="profile-section__name-text">{displayName}</span>
                  {FlagComponent && (
                    <span className="profile-section__country-flag">
                      <FlagComponent width="16" height="16" />
                    </span>
                  )}
                  {hearNameUrl && <HearNameButton url={hearNameUrl} name={displayName} />}
                </h1>
                <p className="profile-section__experience">
                  {experienceLabel(initialTalent?.overview?.totalYearsOfExperience)}
                </p>
              </div>

              {!isMobile && (
                <div className="profile-section__actions">
                  {shortlistMode ? (
                    <SaveToShortlistButton
                      // eslint-disable-next-line no-underscore-dangle
                      talentId={initialTalent?._id}
                      interviewRequested={interviewRequested}
                      size="profile"
                    />
                  ) : (
                    <>
                        {canRequestInterview &&
                          (interviewRequested ? (
                            <CustomButton
                              label="Interview Requested"
                              color="secondary"
                              variant="contained"
                              size="small"
                              startIcon={<CheckIcon />}
                              isDisabled
                              onClick={() => {}}
                            />
                          ) : (
                            <CustomButton
                              label="Request Interview"
                              color="primary"
                              variant="contained"
                              size="small"
                              startIcon={
                                isRequestingInterview ? (
                                  <CircularProgress size={16} sx={{ color: "#fff" }} />
                                ) : (
                                  <svg
                                    width="18"
                                    height="18"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                                    <circle cx="12" cy="9.5" r="3" stroke="currentColor" strokeWidth="1.5" />
                                    <path
                                      d="M6.5 19.5C7 16.5 9.2 14.5 12 14.5C14.8 14.5 17 16.5 17.5 19.5"
                                      stroke="currentColor"
                                      strokeWidth="1.5"
                                      strokeLinecap="round"
                                    />
                                  </svg>
                                )
                              }
                              isDisabled={isRequestingInterview}
                              onClick={handleRequestInterview}
                            />
                          ))}
                      {/* eslint-disable-next-line no-underscore-dangle */}
                      <ShortlistButton talentId={initialTalent?._id} />
                    </>
                  )}
                  {pdfLocked ? (
                    <LockedControl label="Download PDF" icon={<FileDownloadOutlinedIcon />} onClick={lockedClick("pdf")} tip="resume" />
                  ) : (
                      <CustomButton
                        label="Download PDF"
                        color="primary"
                        variant="outlined"
                        size="small"
                        startIcon={<FileDownloadOutlinedIcon />}
                        onClick={handleDownloadPDF}
                      />
                  )}
                </div>
              )}
            </div>

            {/* Badges - placeholder for future use */}
            <div
              className="profile-section__badges"
              style={{ display: hideBadges ? "none" : undefined }}
              aria-hidden={hideBadges}
            />

            {isMobile && (
              <div className="profile-section__actions profile-section__actions--mobile">
                {shortlistMode ? (
                  <SaveToShortlistButton
                    // eslint-disable-next-line no-underscore-dangle
                    talentId={initialTalent?._id}
                    interviewRequested={interviewRequested}
                    size="profile"
                    fullWidth
                  />
                ) : (
                  <>
                    {canRequestInterview &&
                      (interviewRequested ? (
                        <CustomButton
                          label="Interview Requested"
                          color="secondary"
                          isFullWidth
                          variant="contained"
                          size="small"
                          startIcon={<CheckIcon />}
                          isDisabled
                          onClick={() => {}}
                        />
                      ) : (
                        <CustomButton
                          label="Request Interview"
                          color="primary"
                          isFullWidth
                          variant="contained"
                          size="small"
                          startIcon={
                            isRequestingInterview ? (
                              <CircularProgress size={16} sx={{ color: "#fff" }} />
                            ) : (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" />
                                <circle cx="12" cy="9.5" r="3" stroke="currentColor" strokeWidth="1.5" />
                                <path
                                  d="M6.5 19.5C7 16.5 9.2 14.5 12 14.5C14.8 14.5 17 16.5 17.5 19.5"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                />
                              </svg>
                            )
                          }
                          isDisabled={isRequestingInterview}
                          onClick={handleRequestInterview}
                        />
                      ))}
                    {/* eslint-disable-next-line no-underscore-dangle */}
                    <ShortlistButton talentId={initialTalent?._id} />
                  </>
                )}
                {pdfLocked ? (
                  <LockedControl label="Download PDF" icon={<FileDownloadOutlinedIcon />} onClick={lockedClick("pdf")} tip="resume" />
                ) : (
                    <CustomButton
                      label="Download PDF"
                      color="primary"
                      isFullWidth
                      variant="outlined"
                      size="small"
                      startIcon={<FileDownloadOutlinedIcon />}
                      onClick={handleDownloadPDF}
                    />
                )}
              </div>
            )}
          </div>

          {/* Tabs Section */}
          <div className="profile-section__tabs">
            <ProfileTabs tabs={availableTabs} />
          </div>
        </div>

        {/* Content Sections */}
        {initialTalent && <ProfileContent talent={initialTalent} onGlanceExpanded={glanceExpanded} onGlanceFeedback={glanceFeedback} />}
      </div>

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoModalOpen}
        onClose={handleCloseVideo}
        videoUrl={initialTalent?.introVideoUrl || ""}
        talentId={talentId}
        talentName={talentName}
      />

      {/* Interview Success Modal */}
      <InterviewSuccessModal
        isOpen={showInterviewModal}
        onClose={() => setShowInterviewModal(false)}
        showStayHere
        onViewOtherProfiles={() => {
          setShowInterviewModal(false);
          navigate(api.routes.talentNetwork);
        }}
      />
    </>
  );
}

export default ProfileSection;
